/* DCG v2-10-11 · 16 OCT 2026 · item 4: the free-shipping amounts print two decimals unless whole dollars ("$199.40", "$200"; it printed "$199.4"), as sections/header-top.liquid now does; item 6: each dot is named "Slide N" and the dots are no longer hidden from assistive technology (they were focusable buttons inside aria-hidden). Nothing seen changes */
/* =========================================================
   DCG TRUSTBAR
   - Rotation with fade transitions
   - Cart-aware dynamic shipping message
   - Touch swipe (mobile)
   - Dot indicators (desktop)
   - Condense on scroll (opt-in via section settings)
   ========================================================= */

(() => {
  const bar = document.querySelector('[data-dcg-trustbar]');
  if (!bar) return;

  const enabled = bar.dataset.enabled === 'true';
  if (!enabled) return;

  /* =========================
     HEIGHT MEASUREMENT
     Sets --dcg-trustbar-h so the sticky header knows its offset.
     ========================= */

  function measureTrustbar() {
    if (bar.classList.contains('is-condensed')) {
      document.documentElement.style.setProperty('--dcg-trustbar-h', '0px');
      return;
    }
    const h = Math.ceil(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--dcg-trustbar-h', `${h}px`);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', measureTrustbar);
  } else {
    measureTrustbar();
  }

  window.addEventListener('resize', measureTrustbar);

  /* =========================
     MOBILE VISIBILITY
     ========================= */

  const showMobile = bar.dataset.showMobile === 'true';

  function handleMobile() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile && !showMobile) {
      bar.style.visibility = 'hidden';
      bar.style.pointerEvents = 'none';
    } else {
      bar.style.visibility = '';
      bar.style.pointerEvents = '';
    }
    measureTrustbar();
  }

  handleMobile();
  window.matchMedia('(max-width: 767px)').addEventListener('change', handleMobile);

  /* =========================
     ITEMS + DOTS
     ========================= */

  const items = Array.from(bar.querySelectorAll('[data-dcg-trustbar-item]'));
  if (!items.length) return;

  const inner = bar.querySelector('.dcg-trustbar__inner');
  let idx = 0;
  let rotateTimer = null;

  /* Build dot indicators if more than one message */
  let dots = [];
  if (items.length > 1) {
    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'dcg-trustbar__dots';
    // v2-10-11 (item 6): the dots are buttons a keyboard reaches, so they are not hidden from assistive
    // technology; each is named for the message it shows
    items.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'dcg-trustbar__dot';
      dot.type = 'button';
      dot.setAttribute('aria-label', 'Slide ' + (i + 1));
      dot.addEventListener('click', () => goTo(i, true));
      dotsWrap.appendChild(dot);
      dots.push(dot);
    });

    inner.appendChild(dotsWrap);
  }

  function updateDots(i) {
    dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
  }

  /* =========================
     ROTATION
     ========================= */

  const rotate = bar.dataset.rotate === 'true';
  const intervalMs = Math.max(2000, parseInt(bar.dataset.interval || '4', 10) * 1000);
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function goTo(nextIdx, immediate) {
    const current = items[idx];
    const next = items[nextIdx];

    if (prefersReduced || immediate || current === next) {
      items.forEach((el, n) => {
        el.classList.toggle('is-active', n === nextIdx);
        el.classList.remove('is-fading', 'is-visible');
      });
      next.classList.add('is-active', 'is-visible');
      idx = nextIdx;
      updateDots(idx);
      return;
    }

    /* Fade out current */
    current.classList.add('is-fading');
    current.classList.remove('is-visible');

    setTimeout(() => {
      current.classList.remove('is-active', 'is-fading');

      /* Fade in next */
      next.classList.add('is-active');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          next.classList.add('is-visible');
        });
      });

      idx = nextIdx;
      updateDots(idx);
    }, 160);
  }

  function advance() {
    goTo((idx + 1) % items.length);
  }

  /* Initialise first item */
  if (!rotate) {
    items.forEach(el => { el.classList.add('is-active', 'is-visible'); });
    updateDots(0);
  } else {
    items[0].classList.add('is-active', 'is-visible');
    updateDots(0);
    rotateTimer = setInterval(advance, intervalMs);
  }

  /* Pause on hover */
  bar.addEventListener('mouseenter', () => {
    if (rotateTimer) { clearInterval(rotateTimer); rotateTimer = null; }
  });
  bar.addEventListener('mouseleave', () => {
    if (rotate && !rotateTimer) rotateTimer = setInterval(advance, intervalMs);
  });

  /* =========================
     TOUCH SWIPE (mobile)
     ========================= */

  let touchStartX = 0;

  bar.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].clientX;
  }, { passive: true });

  bar.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) < 30) return;

    goTo(dx < 0
      ? (idx + 1) % items.length
      : (idx - 1 + items.length) % items.length,
      true
    );

    if (rotate) {
      if (rotateTimer) clearInterval(rotateTimer);
      rotateTimer = setInterval(advance, intervalMs);
    }
  }, { passive: true });

  /* =========================
     CONDENSE ON SCROLL
     ========================= */

  const condenseEnabled = document.documentElement.dataset.dcgTrustbarCondense === 'true';
  const threshold = parseInt(
    document.documentElement.dataset.dcgTrustbarThreshold || '60', 10
  );

  if (condenseEnabled) {
    const BUFFER = 12;
    let ticking = false;
    let isCondensed = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollPos = window.scrollY;
        if (!isCondensed && scrollPos > threshold) {
          bar.classList.add('is-condensed');
          isCondensed = true;
          measureTrustbar();
        } else if (isCondensed && scrollPos < threshold - BUFFER) {
          bar.classList.remove('is-condensed');
          isCondensed = false;
          measureTrustbar();
        }
        ticking = false;
      });
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* =========================
     DYNAMIC FREE SHIPPING
     ========================= */

  const shippingMsgEl = bar.querySelector('[data-dcg-shipping-msg]');
  const thresholdCents = parseInt(bar.dataset.thresholdCents || '0', 10);

  // v2-10-11 (item 4): two decimals unless the amount is whole dollars: "$199.40", "$200" (as Liquid's
  // money_without_trailing_zeros in sections/header-top.liquid)
  function formatMoney(cents) {
    const c = Math.round(Number(cents) || 0);
    const digits = c % 100 === 0 ? 0 : 2;
    return (c / 100).toLocaleString('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }

  async function refreshShippingMsg() {
    if (!shippingMsgEl || !thresholdCents) return;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      const total = Number(cart.total_price || 0);
      const count = Number(cart.item_count || 0);
      const remaining = thresholdCents - total;
      shippingMsgEl.textContent = (count > 0 && remaining > 0)
        ? `You're ${formatMoney(remaining)} away from free shipping`
        : `Free shipping on orders over ${formatMoney(thresholdCents)}`;
    } catch (_) {}
  }

  ['cart:updated', 'cart:refresh', 'ajaxCart:updated', 'drawer:cart:updated']
    .forEach(evt => document.addEventListener(evt, refreshShippingMsg));

  document.addEventListener('submit', (e) => {
    if (e.target?.matches?.('form[action*="/cart/add"]')) {
      setTimeout(refreshShippingMsg, 700);
    }
  }, true);

  setTimeout(refreshShippingMsg, 300);

})();
